import { APP_STORE_URL, PLAY_STORE_URL } from "@groupeo/shared";
import { useTranslation } from "react-i18next";
import { BreadcrumbJsonLd } from "@/src/common/components/breadcrumb-jsonld";
import { JsonLd } from "@/src/common/components/json-ld";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { env } from "@/src/common/constants/env";
import { AboutSection } from "../components/sections/about-section";
import { SiteShell } from "../components/site-shell";
import { LEGAL_INFORMATION } from "../constants/legal";
import { MARKETING_ROUTES } from "../constants/marketing-routes";

const AboutPage: React.FC = () => {
	const { t } = useTranslation("marketing");
	const pageUrl = `${env.SITE_URL}${MARKETING_ROUTES.ABOUT}`;

	return (
		<>
			<PageHelmet title={t("about_page_title")} description={t("about_page_description")} />
			<BreadcrumbJsonLd
				items={[
					{ name: t("breadcrumb_home"), url: env.SITE_URL },
					{ name: t("nav_about"), url: pageUrl },
				]}
			/>
			<JsonLd
				data={{
					"@context": "https://schema.org",
					"@type": "Organization",
					"@id": `${env.SITE_URL}/#organization`,
					name: env.APP_NAME,
					legalName: LEGAL_INFORMATION.publisherName,
					url: env.SITE_URL,
					logo: `${env.SITE_URL}/logo.png`,
					founder: { "@type": "Person", name: LEGAL_INFORMATION.publicationDirector },
					foundingLocation: "Paris, France",
					address: {
						"@type": "PostalAddress",
						streetAddress: "150 avenue de Versailles",
						addressLocality: "Paris",
						postalCode: "75016",
						addressCountry: "FR",
					},
					contactPoint: [
						{
							"@type": "ContactPoint",
							email: env.CONTACT_EMAIL,
							contactType: "customer support",
							availableLanguage: ["French", "English"],
						},
						{ "@type": "ContactPoint", email: LEGAL_INFORMATION.legalEmail, contactType: "legal" },
					],
					sameAs: [APP_STORE_URL, PLAY_STORE_URL],
				}}
			/>
			<SiteShell>
				<main className="overflow-hidden">
					<div className="mx-auto max-w-5xl px-6 pb-24 pt-8 sm:px-8 lg:px-12">
						<header className="max-w-3xl py-12">
							<p className="text-sm font-semibold uppercase tracking-[0.24em] text-app-accent-secondary">{t("nav_about")}</p>
							<h1 className="mt-4 text-4xl font-semibold tracking-[-0.06em] text-white sm:text-6xl">{t("about_page_title")}</h1>
							<p className="mt-5 text-lg leading-8 text-white/70">{t("about_page_description")}</p>
						</header>
						<AboutSection />
						<section className="mt-10 grid gap-6 md:grid-cols-2">
							<article className="rounded-[1.75rem] border border-white/10 bg-white/6 p-6">
								<h2 className="text-xl font-semibold text-white">{t("about_page_mission_title")}</h2>
								<p className="mt-3 text-sm leading-7 text-white/70">{t("about_page_mission_body")}</p>
							</article>
							<article className="rounded-[1.75rem] border border-white/10 bg-white/6 p-6">
								<h2 className="text-xl font-semibold text-white">{t("about_page_publisher_title")}</h2>
								<p className="mt-3 text-sm leading-7 text-white/70">{t("about_page_publisher_body")}</p>
							</article>
							<article className="rounded-[1.75rem] border border-white/10 bg-white/6 p-6 md:col-span-2">
								<h2 className="text-xl font-semibold text-white">{t("about_page_contact_title")}</h2>
								<p className="mt-3 text-sm leading-7 text-white/70">{t("about_page_contact_body")}</p>
							</article>
						</section>
					</div>
				</main>
			</SiteShell>
		</>
	);
};

export default AboutPage;
